import app from '../../forum/app';
import subclassOf from '../../common/utils/subclassOf';
import Stream from '../../common/utils/Stream';
import ReplyComposer from '../components/ReplyComposer';
import EditPostComposer from '../components/EditPostComposer';

class ComposerState {
  constructor() {
    /**
     * The composer's current position.
     *
     * @type {ComposerState.Position}
     */
    this.position = ComposerState.Position.HIDDEN;

    /**
     * The composer's intended height, which can be modified by the user
     * (by dragging the composer handle).
     *
     * @type {number}
     */
    this.height = null;

    /**
     * The dynamic component being shown inside the composer.
     *
     * @type {Object}
     */
    this.body = { attrs: {} };

    /**
     * A reference to the text editor that allows text manipulation.
     *
     * @type {import('../../common/utils/EditorDriverInterface')|null}
     */
    this.editor = null;

    /**
     * In-memory drafts keyed by composer context.
     * This is NOT persisted; a page refresh will clear everything.
     *
     * @type {Map<string, {content: string, title?: string}>}
     */
    this.drafts = new Map();

    this.clear();
  }

  /**
   * Build an in-memory key for the current composer context.
   * - ReplyComposer: per discussion + per reply target (楼层/楼中楼)
   * - Others: per component name
   */
  draftKeyFor(componentClass, attrs = {}) {
    const userId = app.session?.user?.id?.() ?? 'guest';

    if (componentClass && subclassOf(componentClass, ReplyComposer)) {
      const discussionId =
        attrs?.discussion?.id?.() ??
        attrs?.post?.discussion?.id?.() ??
        'unknown';

      // CommentTree-lin / evergreen:
      // - replyPost: 当前具体楼中楼评论 ID（每个回复框需要独立）
      // - replyTo: 根评论 / 父楼层 ID
      const replyTargetId =
        attrs?.replyPost ??
        attrs?.replyTo ??
        attrs?.replyToPost?.id?.() ??
        attrs?.post?.id?.() ??
        attrs?.replyToPostId ??
        attrs?.replyToId ??
        null;

      const target = replyTargetId ? `post:${replyTargetId}` : 'root';

      return `reply:${userId}:${discussionId}:${target}`;
    }

    const name = componentClass?.name || 'unknown';
    return `generic:${userId}:${name}`;
  }

  saveDraftForCurrent() {
    const componentClass = this.body?.componentClass;
    const attrs = this.body?.attrs || {};
    if (!componentClass || !this.fields || subclassOf(componentClass, EditPostComposer)) return;

    const content = (this.fields.content?.() ?? '').toString();
    const title = (this.fields.title?.() ?? '').toString();

    const hasAny =
      (content && content.trim().length > 0) ||
      (title && title.trim().length > 0);

    const key = this.draftKeyFor(componentClass, attrs);

    if (!hasAny) {
      this.drafts.delete(key);
      return;
    }

    this.drafts.set(key, { content, title });
  }

  loadDraft(componentClass, attrs) {
    if (componentClass && subclassOf(componentClass, EditPostComposer)) return;

    const key = this.draftKeyFor(componentClass, attrs);
    return this.drafts.get(key);
  }

  clearDraft(componentClass, attrs) {
    if (componentClass && subclassOf(componentClass, EditPostComposer)) return;

    const key = this.draftKeyFor(componentClass, attrs);
    this.drafts.delete(key);
  }

  /**
   * Load a content component into the composer.
   *
   * @param {typeof import('../components/ComposerBody').default} componentClass
   */
  load(componentClass, attrs) {
    const body = { componentClass, attrs };

    // Before switching context, remember the current draft in memory.
    this.saveDraftForCurrent();

    // If another composer body is already loaded (even if hidden via soft-close)
    // and we are switching to a different context, clear out old state/editor
    // to avoid leaking editor instances across composer types.
    if (this.body?.componentClass && !this.bodyMatches(componentClass, attrs)) {
      this.clear();
      m.redraw.sync();
    }

    // If we load a similar component into the composer, then Mithril will be
    // able to diff the old/new contents and some DOM-related state from the
    // old composer will remain. To prevent this from happening, we clear the
    // component and force a redraw, so that the new component will be working
    // on a blank slate.
    if (this.isVisible()) {
      this.clear();
      m.redraw.sync();
    }

    // Restore draft for this specific context (discussion + floor / nested post)
    const draft = this.loadDraft(componentClass, attrs);
    if (draft && typeof draft.content === 'string') {
      body.attrs.originalContent = draft.content;
    }

    this.body = body;
  }

  /**
   * Clear the composer's content component.
   */
  clear() {
    this.position = ComposerState.Position.HIDDEN;
    this.body = { attrs: {} };
    this.onExit = null;

    this.fields = {
      content: Stream(''),
    };

    if (this.editor) {
      this.editor.destroy();
    }
    this.editor = null;
  }

  /**
   * Show the composer.
   */
  show() {
    if (this.position === ComposerState.Position.NORMAL || this.position === ComposerState.Position.FULLSCREEN) return;

    this.position = ComposerState.Position.NORMAL;
    m.redraw.sync();
  }

  /**
   * Hard-hide the composer (used after successful submit).
   * This will destroy the component/editor and clear fields.
   */
  hide() {
    // On successful submit, drop any in-memory draft for this context.
    if (this.body?.componentClass) {
      this.clearDraft(this.body.componentClass, this.body.attrs || {});
    }
    this.clear();
    m.redraw();
  }

  /**
   * Close the composer without destroying it.
   * We just hide it so that unsent content is kept in memory (per-key).
   */
  close() {
    // Save current draft for this context in memory so each comment's reply box is independent.
    this.saveDraftForCurrent();

    //修改关闭编辑后样式依然处于编辑中的问题
    this.body = { attrs: {} };
    this.onExit = null;

    if (this.editor) {
      this.editor.destroy();
    }
    this.editor = null;
    this.fields = {
      content: Stream(''),
    };

    this.position = ComposerState.Position.HIDDEN;
    m.redraw();
  }

  /**
   * Minimize the composer. Has no effect if the composer is hidden.
   */
  minimize() {
    if (!this.isVisible()) return;

    this.position = ComposerState.Position.MINIMIZED;
    m.redraw();
  }

  /**
   * Take the composer into fullscreen mode. Has no effect if the composer is
   * hidden.
   */
  fullScreen() {
    if (!this.isVisible()) return;

    this.position = ComposerState.Position.FULLSCREEN;
    m.redraw();
  }

  /**
   * Exit fullscreen mode.
   */
  exitFullScreen() {
    if (this.position !== ComposerState.Position.FULLSCREEN) return;

    this.position = ComposerState.Position.NORMAL;
    m.redraw();
  }

  /**
   * Determine whether the body matches the given component class and data.
   *
   * @param {object} type The component class to check against. Subclasses are accepted as well.
   * @param {object} data
   * @return {boolean}
   */
  bodyMatches(type, data = {}) {
    // Fail early when the body is of a different type
    if (!subclassOf(this.body.componentClass, type)) return false;

    // Now that the type is known to be correct, we loop through the provided
    // data to see whether it matches the data in the attributes for the body.
    return Object.keys(data).every((key) => this.body.attrs[key] === data[key]);
  }

  /**
   * Determine whether or not the Composer is visible.
   *
   * True when the composer is displayed on the screen and has a body component.
   * It could be open in "normal" or full-screen mode, or even minimized.
   *
   * @returns {boolean}
   */
  isVisible() {
    return this.position !== ComposerState.Position.HIDDEN;
  }

  /**
   * Determine whether or not the Composer is covering the screen.
   *
   * This will be true if the Composer is in full-screen mode on desktop,
   * or if we are on a mobile device, where we always consider the composer as full-screen..
   *
   * @return {boolean}
   */
  isFullScreen() {
    return this.position === ComposerState.Position.FULLSCREEN || app.screen() === 'phone';
  }

  /**
   * Check whether or not the user is currently composing a reply to a
   * discussion.
   *
   * @param {import('../../common/models/Discussion').default} discussion
   * @return {boolean}
   */
  composingReplyTo(discussion) {
    return this.isVisible() && this.bodyMatches(ReplyComposer, { discussion });
  }

  /**
   * Confirm with the user that they want to close the composer and lose their
   * content.
   *
   * @return {boolean} Whether or not the exit was cancelled.
   */
  preventExit() {
    if (!this.isVisible()) return;
    if (!this.onExit) return;

    if (this.onExit.callback()) {
      return !confirm(this.onExit.message);
    }
  }

  /**
   * Configure when / what to ask the user before closing the composer.
   *
   * The provided callback will be used to determine whether asking for
   * confirmation is necessary. If the callback returns true at the time of
   * closing, the provided text will be shown in a standard confirmation dialog.
   *
   * @param {() => boolean} callback
   * @param {string} message
   */
  preventClosingWhen(callback, message) {
    this.onExit = { callback, message };
  }

  /**
   * Minimum height of the Composer.
   * @returns {number}
   */
  minimumHeight() {
    return 200;
  }

  /**
   * Maxmimum height of the Composer.
   * @returns {number}
   */
  maximumHeight() {
    return $(window).height() - $('#header').outerHeight();
  }

  /**
   * Computed the composer's current height, based on the intended height, and
   * the composer's current state. This will be applied to the composer
   * content's DOM element.
   * @returns {number | string}
   */
  computedHeight() {
    // If the composer is minimized, then we don't want to set a height; we'll
    // let the CSS decide how high it is. If it's fullscreen, then we need to
    // make it as high as the window.
    if (this.position === ComposerState.Position.MINIMIZED) {
      return '';
    } else if (this.position === ComposerState.Position.FULLSCREEN) {
      return $(window).height();
    }

    // Otherwise, if it's normal or hidden, then we use the intended height.
    // We don't let the composer get too small or too big, though.
    return Math.max(this.minimumHeight(), Math.min(this.height, this.maximumHeight()));
  }
}

ComposerState.Position = {
  HIDDEN: 'hidden',
  NORMAL: 'normal',
  MINIMIZED: 'minimized',
  FULLSCREEN: 'fullScreen',
};

export default ComposerState;
